import Dashboard from "../../dashboard/Dashboard";
import { NavLink, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Spinner } from "../../../components";
import "./style.scss";
import {
  listPayoutByStatus,
  updatePayoutStatus,
} from "../../../services/payout.service";
import { IWithdrawal, WithdrawResponse } from "../../reviews/types";
import showToast from "../../../utils/show.toast";
import {
  ArrowLeft,
  Calendar1Icon,
  CheckCheckIcon,
  DownloadIcon,
  SearchIcon,
} from "lucide-react";

interface IWithdrawalData {
  isLoading: boolean;
  withdrawal: IWithdrawal[];
}

export default function RegularPayoutTable() {
  const [withdrawalData, setWithdrawalData] = useState<IWithdrawalData>({
    isLoading: true,
    withdrawal: [],
  });

  const [SearchParams] = useSearchParams();
  const withdrawalStatus = SearchParams.get("status");

  const [isActive, setIsActive] = useState<boolean>(false);
  const [selectedWithdrawalIds, setSelectedWithdrawalIds] = useState<string[]>(
    []
  );

  const [selectMarkAll, setSelectMarkAll] = useState<boolean>(false);

  const calculatePayoutAmount = (money: string) => {
    const withdrawalAmount = parseFloat(money);

    if (isNaN(withdrawalAmount) || withdrawalAmount < 0) {
      return "invalid amount";
    }

    const payoutAmount = withdrawalAmount * 0.92;
    return payoutAmount.toFixed(2);
  };

  const handleCheckboxChange = (id: string) => {
    setSelectedWithdrawalIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((withdrawId) => withdrawId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleCheck = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const payload = { withdrawalIds: selectedWithdrawalIds, status: "paid" };
    if (payload.withdrawalIds.length === 0) {
      return;
    }

    try {
      const statusUpate = await updatePayoutStatus(
        "withdrawal/batch-status",
        payload
      );
      if (statusUpate.status !== "success") {
        showToast("Error marking status paid", false);
        return;
      }
      window.location.reload();
      return showToast("Payout was successful.", true);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Apologies, an error occured.";
      return showToast(msg, false);
    }
  };

  const handleSelectMarkAll = () => {
    if (selectMarkAll) {
      setSelectedWithdrawalIds([]);
    } else {
      const ids = withdrawalData.withdrawal.map(
        (withdrawal) => withdrawal.withdrawalId
      );
      setSelectedWithdrawalIds(ids);
    }

    setSelectMarkAll(!selectMarkAll);
  };

  useEffect(() => {
    setIsActive(selectedWithdrawalIds.length > 0);
  }, [selectedWithdrawalIds]);

  useEffect(() => {
    if (withdrawalStatus) {
      fetchWithdrawalByStatus(withdrawalStatus);
    }
  }, [withdrawalStatus]);

  const fetchWithdrawalByStatus = async (status: string) => {
    try {
      const response = await listPayoutByStatus<WithdrawResponse>(
        `withdrawal/?status=${status}`
      );
      if (response.status !== "success") {
        setWithdrawalData({ withdrawal: [], isLoading: false });
        return showToast("Error fetching withdraw!", false);
      }

      setWithdrawalData({ withdrawal: response.result, isLoading: false });
    } catch (error) {
      setWithdrawalData({ withdrawal: [], isLoading: false });
      return showToast("Error fetching withdraws!", false);
    }
  };

  return (
    <Dashboard>
      <div className="dashboard-header-s">
        <div className="dashboard-title">
          <NavLink to="/payout/dashboard" className="back-link">
            <ArrowLeft className="back-icon" size={20} />
            <span>Regular Requests</span>
          </NavLink>
        </div>
        <p className="dashboard-date">
          <Calendar1Icon className="date-icon" />
          {new Date().toUTCString()}
        </p>
      </div>

      <div className="stat-cards">
        <div className="stat-card">
          <p>Total Withdrawal Amount</p>
          <h2>$0</h2>
        </div>
        <div className="stat-card">
          <p>Total Payout Amount</p>
          <h2>$0</h2>
        </div>
        <div className="stat-card">
          <p>Total Request</p>
          <h2>0</h2>
        </div>
      </div>

      <div className="dashboard-actions">
        <div className="search-bar">
          <SearchIcon />
          <input placeholder="Search by Email" />
        </div>
        <div className="action-buttons">
          <form onSubmit={handleCheck}>
            <button
              type="submit"
              className={`mark-paid ${isActive ? "active" : ""}`}
            >
              Mark as Paid
            </button>
          </form>
          <button className="download-btn">
            Download <DownloadIcon />
          </button>
        </div>
      </div>

      {withdrawalData.isLoading ? (
        <Spinner />
      ) : (
        <div className="dashboard-table">
          <table>
            <thead>
              <tr>
                <th>
                  <CheckCheckIcon />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Provider Type</th>
                <th>Withdrawal Amt</th>
                <th>Account Reference</th>
                <th>Payout Amount (USD)</th>
                <th>Status</th>
                <th>Request Date</th>
              </tr>
            </thead>
            <tbody>
              {withdrawalData.withdrawal.map((pay) => (
                <tr key={pay.teacherProfileId}>
                  <td>
                    <input
                      type="checkbox"
                      onChange={() => handleCheckboxChange(pay.withdrawalId)}
                    />
                  </td>
                  <td>
                    {pay.firstName} {pay.lastName} {pay.otherNames}
                  </td>
                  <td>{pay.email}</td>
                  <td>{pay.amount} USD</td>
                  <td>{pay.paymentMethodType}</td>
                  <td>
                    {pay.paymentMethodType === "paypal"
                      ? pay.paymentDetails.email
                      : pay.paymentDetails.payoneerId}{" "}
                    - {pay.paymentMethodType.toUpperCase()}
                  </td>
                  <td>{calculatePayoutAmount(pay.amount)} USD</td>
                  <td>
                    <span className={`status ${pay.status.toLowerCase()}`}>
                      {pay.status}
                    </span>
                  </td>

                  <td>{new Date(pay.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dashboard>
  );
}
